import React, {useEffect, useState} from "react";
import {useQuery} from "@apollo/client";
import {GET_CURRENT_USER} from "./queries";
import styles from "../../TalentApplication/styles.module.css";
import Spinner from "../../../assets/Spinner/Spinner";
import {useAuth0} from "@auth0/auth0-react";
import logoBlack from "../../../assets/images/schmodelplain-withR-black.png";
import {FiLogOut} from "react-icons/fi";
import * as FullStory from '@fullstory/browser';
import {Redirect, withRouter} from "react-router-dom";

const WithUserDataWrapper = ({location, children}) => {
    const {isLoading: auth0Loading, error: auth0Error, getAccessTokenSilently, loginWithRedirect, logout} = useAuth0();
    const [token, setToken] = useState(localStorage.getItem("token"));
    const [appIsLoading, setAppIsLoading] = useState(true);
    const [showSpinner, setShowSpinner] = useState(false);

    const { pathname } = location;
    const pathsRequiringMembership = [
        "/club",
        "/shop",
        "/notifications",
    ];

    const {data, loading: queryLoading, error} = useQuery(GET_CURRENT_USER, {
        skip: !token, // Only query if we have a token
        onCompleted: (data) => {
          if (data.currentUser) {
            FullStory.identify(data.id, data.currentUser);
          }
        }
    });

    const isInvalidMagicLink = () => {
        if(!auth0Error?.error_description) return false;

        const err = auth0Error.error;
        const desc = auth0Error.error_description.toLowerCase();

        return (
            // Case 1: magic link expired
            // Expected description: "The verification code has expired. Please try to login again."
            (err === "unauthorized" && desc.includes("code has expired")) ||

            // Case 2: too many invalid attempts, multiple failed uses of the same magic link.
            // Expected description: "You've reached the maximum number of attempts. Please try to login again."
            (err === "unauthorized" && desc.includes("maximum number of attempts")) ||

            // Case 3: The session and magic link state match, but the link has already been consumed.
            // Expected description: "Wrong email or verification code."
            (err === "unauthorized" && desc.includes("wrong email or verification code"))

            //TODO: In some cases, we see this error even when the login starts in the app and the magic link is opened in the app.
            // Case 4: the link is opened in a different browser (no session)
            // or when the session exists but the stored state doesn’t match the link's state (old magic link)
            // Expected description: "Invalid state"
            // (err === "missing_transaction" && desc === "invalid state")
        );
    }
    const isUsernameAndPasswordAuthDenied = () => {
        const params = new URLSearchParams(window.location.search);
        const error = params.get("error");
        const error_description = params.get("error_description");

        return  error === "access_denied" && error_description === "Username and password auth is not allowed";
    }

    const fetchAuth0Token = async () => {
        // Fetch auth0 token in the background
        try {
            const newToken = await getAccessTokenSilently();
            localStorage.setItem("token", newToken);
            setToken(newToken);
        } catch (err) {
            if (isInvalidMagicLink() || isUsernameAndPasswordAuthDenied()) {
                // Magic link is not valid or Username and password authentication —> display a message to the user without redirecting to login
                setAppIsLoading(false)
            }
            else {
                // Redirect to login if we fail to generate auth0 access token
                await loginWithRedirect();
            }
        }
    };
    const handleLogout = async () => {
        localStorage.removeItem("token");
        logout({
            logoutParams: {
                returnTo: `${window.location.origin}/account`
            }
        });
    };

    useEffect(() => {
        // Wait until Auth0 finishes loading before proceeding
        if (auth0Loading) return;
        // If no token, try getting it silently from Auth0
        if(!token){
            fetchAuth0Token();
        } else {
            setAppIsLoading(false)
        }
    }, [token, auth0Loading]);

    useEffect(() => {
        // JWT Token expired, empty token to refetch a new one
        if (error?.graphQLErrors?.[0]?.extensions?.reason === 'JWT_EXPIRED') {
            localStorage.removeItem("token");
            setToken(undefined);
            setAppIsLoading(true);
        }
    }, [error]);

    // Delay spinner display to avoid quick flashes on fast transitions
    useEffect(() => {
        let timeout;
        if (auth0Loading || queryLoading || appIsLoading) {
            timeout = setTimeout(() => setShowSpinner(true), 200);
        } else {
            setShowSpinner(false);
        }
        return () => clearTimeout(timeout);
    }, [auth0Loading, queryLoading, appIsLoading]);

    let content;
    // auth0Loading: Auth0 SDK is still initializing
    // userDataLoading: Fetching user data from backend
    // appIsLoading: Session check in progress (token being fetched or redirecting to login if missing/invalid)
    if (auth0Loading || queryLoading || appIsLoading) {
        content = !showSpinner ? <></> :(
            <div className={`w-full h-full ${styles.Loading}`}>
                <Spinner color='black' />
            </div>
        );
    }
    else if(isInvalidMagicLink() && !token){
        // Handle Auth0 errors that should be shown to the user (magic link expired, ...)
        content = (
            <div
                className={`w-full h-full flex flex-col items-center pt-20`}
            >
                <p className={`${styles.error_message}  w-3/4 mx-auto`}>
                    This link is no longer valid. Try logging in again to get a new one
                </p>
                <div
                    className="cursor-pointer w-3/4 py-3 bg-gray-300 text-black rounded-xl flex items-center justify-center mt-3 mx-auto hover:bg-gray-400"
                    onClick={loginWithRedirect}
                >
                    <span>Login</span>
                </div>
            </div>
        )
    }
    else if(isUsernameAndPasswordAuthDenied() && !token){
        // We show this block when a user (different from app reviewer) tries username password auth
        content = (
            <div
                className={`w-full h-full flex flex-col items-center pt-20`}
            >
                <p className={`${styles.error_message}  w-3/4 mx-auto`}>
                    Username and password auth is not allowed
                </p>
                <div
                    className="cursor-pointer w-3/4 py-3 bg-gray-300 text-black rounded-xl flex items-center justify-center mt-3 mx-auto hover:bg-gray-400"
                    // We need to clear user session to be able to login with a different user
                    // https://community.auth0.com/t/how-to-handle-access-denied-response/136668/3
                    onClick={handleLogout}
                >
                    <span>Login</span>
                </div>
                <p className={`text-center text-red-500  w-3/4 mx-auto break-words`}>
                    error: {auth0Error.error} <br/>
                    description: {auth0Error.error_description} <br/>
                    url: {window.location.href}
                </p>
            </div>
        )
    }
    else if(!data?.currentUser){
        // This block handles cases where backend token verification fails
        // (malformed token or user not found in the MongoDB database)
        content = (
            <div
                className={`w-full h-full flex flex-col justify-end items-center`}
            >
                <div
                    className="cursor-pointer w-3/4 py-3 bg-gray-300 text-black rounded-xl flex items-center mb-16 mx-auto hover:bg-gray-400"
                    onClick={handleLogout}
                >
                    <div className="w-24 flex items-center justify-center">
                        <FiLogOut size={22}/>
                    </div>
                    <span>Log out</span>
                </div>
            </div>
        )
    }
    else {
        if(!data?.currentUser?.membership && pathsRequiringMembership.includes(pathname)){
            return <Redirect to="/membership-missing" />;
        }
        content = children({user: data?.currentUser, logout: handleLogout});
    }
    return (
        <div className="bg-white flex justify-center top-0 left-0 right-0 bottom-0 overflow-auto font-inter" style={{ height: "100vh", width: "100vw" }}>
            <div className="h-full w-full flex flex-col pt-2 sm:pt-10 pb-0 sm:pb-10 w-sm-380" >
                {content}
            </div>
        </div>
    );
};
export default withRouter(WithUserDataWrapper);

